<template>
    <div>
        <ModalBody>
            <div v-if="type === 'rental'">
                <ModalHeader>
                    <ModalTitle>대여 관리 팝업</ModalTitle>
                </ModalHeader>

                <ModalSubtitle>
                    * 이번 달에 만료가 되는 대여 정보입니다.
                </ModalSubtitle>

                <!-- * 탭 메뉴 -->
                <TabContainer>
                    <TabButton :class="{ active: currentTab === 'basic' }" @click="changeTab('basic')">
                        일반대여 ({{ basicRentalData.length }})
                    </TabButton>
                    <TabButton :class="{ active: currentTab === 'oxygen' }" @click="changeTab('oxygen')">
                        산소발생기 ({{ oxygenRentalData.length }})
                    </TabButton>
                    <TabButton :class="{ active: currentTab === 'assistance' }" @click="changeTab('assistance')">
                        보장구 ({{ assistanceRentalData.length }})
                    </TabButton>
                    <TabButton :class="{ active: currentTab === 'diabetes' }" @click="changeTab('diabetes')">
                        당뇨소모성재료 ({{ diabetesRentalData.length }})
                    </TabButton>
                </TabContainer>

                <!-- * 데이터 표시 -->
                <RentalGrid v-if="currentRentalData && currentRentalData.length > 0">
                    <RentalCard v-for="rental in currentRentalData" :key="rental.num">
                        <RentalHeader>
                            <CustomerName :style="isTestUser ? { filter: 'blur(4px)' } : {}">{{ rental.name }}
                            </CustomerName>
                            <ProductName>{{ rental.product_name }}</ProductName>
                        </RentalHeader>
                        <RentalDates>
                            <DateItem>
                                <DateLabel>연락처</DateLabel>
                                <DateValue :style="isTestUser ? { filter: 'blur(4px)' } : {}">{{
                                    formatPhoneNumber(rental.phone1 || rental.phone2 || '-') }}</DateValue>
                            </DateItem>
                            <DateItem>
                                <DateLabel>대여 시작일</DateLabel>
                                <DateValue>{{ formatDate(rental.rental_start) }}</DateValue>
                            </DateItem>
                            <DateItem>
                                <DateLabel>대여 종료일</DateLabel>
                                <DateValue>{{ formatDate(rental.rental_end) }}</DateValue>
                            </DateItem>
                            <DateItem>
                                <DateLabel>가상계좌 발송</DateLabel>
                                <DateValue>
                                    <AccountButton @click="openAccountSendModal(rental)">
                                        <SendVariant :size="15" />발송
                                    </AccountButton>
                                </DateValue>
                            </DateItem>
                            <DateItem>
                                <DateLabel>문자 발송</DateLabel>
                                <DateValue>
                                    <AccountButton @click="openSmsSendModal(rental)">
                                        <SendVariant :size="15" />발송
                                    </AccountButton>
                                </DateValue>
                            </DateItem>
                        </RentalDates>
                        <ExtendButton @click="handleExtend(rental)">
                            연장하기
                        </ExtendButton>
                    </RentalCard>
                </RentalGrid>

                <!-- * 데이터가 없을 때 표시 -->
                <NoDataSection v-else>
                    대여 정보가 없습니다.
                </NoDataSection>
            </div>

            <!-- ! 재고 관리 팝업 -->
            <div v-else-if="type === 'inventory'">
                <ModalHeader>
                    <ModalTitle>안전재고 관리 팝업</ModalTitle>
                </ModalHeader>

                <ModalSubtitle>
                    * 수량이 3개 이하인 재고 정보입니다.
                </ModalSubtitle>

                <SearchWrapper>
                    <SearchForm @submit.prevent="handleSearch">
                        <SearchInputWrapper>
                            <SearchInput type="text" v-model="searchKeyword"
                                placeholder="회사명으로 검색 후에 엔터 키나 검색 버튼을 클릭해주세요." @keyup.enter="handleSearch" />
                            <SearchButton>
                                <Magnify />
                            </SearchButton>
                        </SearchInputWrapper>
                    </SearchForm>
                </SearchWrapper>

                <!-- * 데이터 표시 -->
                <BulkOrderGrid v-if="filteredInventoryData && Object.keys(filteredInventoryData).length > 0">
                    <CompanySection v-for="(items, companyName) in filteredInventoryData" :key="companyName">
                        <CompanyHeader>
                            <CompanyTitle>{{ companyName }}</CompanyTitle>
                            <BulkOrderButton @click="handleBulkOrder(companyName, items)">
                                일괄발주
                            </BulkOrderButton>
                        </CompanyHeader>

                        <ProductGrid>
                            <InventoryCard v-for="(item, index) in items" :key="index">
                                <InventoryHeader>
                                    <div>{{ item.product_name }}</div>
                                </InventoryHeader>
                                <InventoryDetails>
                                    <DetailItem>
                                        <DetailLabel>색상</DetailLabel>
                                        <DetailValue>{{ item.color }}</DetailValue>
                                    </DetailItem>
                                    <DetailItem>
                                        <DetailLabel>사이즈</DetailLabel>
                                        <DetailValue>{{ item.size }}</DetailValue>
                                    </DetailItem>
                                    <DetailItem>
                                        <DetailLabel>재고수량</DetailLabel>
                                        <StockValue :isLow="item.stock_quantity <= item.safety_stock">
                                            {{ item.stock_quantity }}
                                        </StockValue>
                                    </DetailItem>
                                </InventoryDetails>
                                <OrderButton @click="handleOrder(item)">
                                    발주신청
                                </OrderButton>
                            </InventoryCard>
                        </ProductGrid>
                    </CompanySection>
                </BulkOrderGrid>

                <!-- * 데이터가 없을 때 표시 -->
                <NoDataSection v-else>
                    안전재고 정보가 없습니다.
                </NoDataSection>
            </div>
        </ModalBody>

        <ModalFooter>
            <DontShowTodayCheckboxWrapper>
                <input type="checkbox" v-model="dontShowTodayChecked" />
                하루동안 보지 않기
            </DontShowTodayCheckboxWrapper>
            <ModalFooterCloseButton @click="handleHideToday">
                닫기
            </ModalFooterCloseButton>
        </ModalFooter>
    </div>

    <!-- !가상계좌 발송 모달 -->
    <ModalPage v-model:show="showModal_obj.virtual_up" :width="modal_width.virtual_up"
        :height="modal_height.virtual_up">
        <AccountSendModal :customerName="selectedCustomer.name" :customerPhone="selectedCustomer.phone"
            :isTestUser="selectedCustomer.isTestUser" @close="showModal_obj.virtual_up = false" />
    </ModalPage>

    <!-- !문자 발송 모달 -->
    <SmsSendModal v-if="showModal_obj.sms_up" :width="modal_width.sms_up" :height="modal_height.sms_up"
        :recipient="selectedCustomer" @close="showModal_obj.sms_up = false" />
</template>

<script>
import { reactive, ref } from 'vue';

import * as AlertModalCss from '@/assets/styles/global/AlertModalCss';
import ModalPage from '@/components/layout/ModalPage.vue';
import AccountSendModal from '@/components/account/modal/AccountSendModal.vue';
import SmsSendModal from '@/components/Manage/modal/SmsSendModal.vue';

import SendVariant from 'vue-material-design-icons/SendVariant.vue';
import Magnify from 'vue-material-design-icons/Magnify.vue';

export default {
    name: 'AlertModal',

    components: {
        ...AlertModalCss,
        ModalPage,
        AccountSendModal,
        SendVariant, Magnify,
        SmsSendModal
    },

    setup() {
        const searchKeyword = ref(''); // 검색어 입력값
        const searchCompany = ref(''); // 실제 검색에 사용될 값

        const showModal_obj = reactive({
            virtual_up: false,
            sms_up: false,
        });

        const modal_width = {
            virtual_up: '25%',
            sms_up: '50%',
        };

        const modal_height = {
            virtual_up: '80%',
            sms_up: '80%',
        };

        return {
            searchKeyword,
            searchCompany,
            showModal_obj,
            modal_width,
            modal_height
        }
    },

    props: {
        show: {
            type: Boolean,
            required: true
        },
        type: {
            type: String,
            required: true,
            validator: (value) => ['rental', 'inventory'].includes(value)
        },
        title: {
            type: String,
            default: '알림'
        },
        safetyStockItems: {
            type: Array,
            default: () => []
        }
    },

    data() {
        return {
            rentalData: [],
            inventoryData: [],
            selectedMonths: {},
            // 탭 관련
            currentTab: 'basic',
            basicRentalData: [],
            oxygenRentalData: [],
            assistanceRentalData: [],
            diabetesRentalData: [],
            selectedCustomer: {
                name: '',
                phone: ''
            },
            dontShowTodayChecked: false
        }
    },

    emits: ['close', 'extend'],

    computed: {
        isTestUser() {
            try {
                const userInfo = JSON.parse(localStorage.getItem('userInfo'));
                return userInfo && userInfo.id === 'test';
            } catch (error) {
                return false;
            }
        },
        filteredInventoryData() {
            if (!this.searchCompany) {
                return this.groupedInventoryData;
            }

            const filteredData = {};
            Object.entries(this.groupedInventoryData).forEach(([company, items]) => {
                if (company.toLowerCase().includes(this.searchCompany.toLowerCase())) {
                    filteredData[company] = items;
                }
            });
            return filteredData;
        },

        groupedInventoryData() {
            return this.inventoryData.reduce((acc, item) => {
                const company = item.company_name || '미지정';
                if (!acc[company]) {
                    acc[company] = [];
                }
                acc[company].push(item);
                return acc;
            }, {});
        },

        currentRentalData() {
            let data = [];
            switch (this.currentTab) {
                case 'basic':
                    data = this.basicRentalData;
                    break;
                case 'oxygen':
                    data = this.oxygenRentalData;
                    break;
                case 'assistance':
                    data = this.assistanceRentalData;
                    break;
                case 'diabetes':
                    data = this.diabetesRentalData;
                    break;
            }
            return data;
        },
    },

    methods: {
        handleHideToday() {
            if (this.dontShowTodayChecked) {
                // 하루동안 보지 않기 체크된 경우에만 저장
                const userId = this.$globalState?.user_id || 'default';
                const key = `alertModalHide_${this.type}_${userId}`;
                const today = new Date().toISOString().slice(0, 10);
                localStorage.setItem(key, today);
            }
            this.$emit('close');
        },

        openSmsSendModal(rental) {
            this.selectedCustomer = {
                name: this.isTestUser ? '테스트' : rental.name,
                phone: this.isTestUser ? '010-0000-0000' : (rental.phone1 || rental.phone2 || ''),
                isTestUser: this.isTestUser // 블러 처리 정보 전달
            };
            this.showModal_obj.sms_up = true;
        },

        openAccountSendModal(rental) {
            this.selectedCustomer = {
                name: this.isTestUser ? '테스트' : rental.name,
                phone: this.isTestUser ? '010-0000-0000' : (rental.phone1 || rental.phone2 || ''),
                isTestUser: this.isTestUser // 블러 처리 정보 전달
            };
            this.showModal_obj.virtual_up = true;
        },
        closeAccountSendModal() {
            this.showModal_obj.virtual_up = false;
            this.selectedCustomer = {
                name: '',
                phone: ''
            };
        },
        changeTab(tab) {
            this.currentTab = tab;
            // 이미 데이터가 있다면 다시 불러오지 않음
            if (this.getCurrentTabData(tab).length === 0) {
                this.fetchRentalData(tab);
            }
        },
        getCurrentTabData(tab) {
            switch (tab) {
                case 'basic':
                    return this.basicRentalData;
                case 'oxygen':
                    return this.oxygenRentalData;
                case 'assistance':
                    return this.assistanceRentalData;
                case 'diabetes':
                    return this.diabetesRentalData;
                default:
                    return [];
            }
        },
        formatDate(date) {
            if (!date) return '';
            const d = new Date(date);
            return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        },
        formatPhoneNumber(phoneNumber) {
            if (!phoneNumber) return '';
            if (phoneNumber.startsWith('010')) {
                return phoneNumber.replace(/(\d{3})(\d{4})(\d{4})/, '$1-$2-$3');
            } else if (phoneNumber.startsWith('02')) {
                return phoneNumber.replace(/(\d{2})(\d{3})(\d{4})/, '$1-$2-$3');
            } else {
                return phoneNumber;
            }
        },
        selectMonth(rentalIndex, month) {
            this.selectedMonths = {
                ...this.selectedMonths,
                [rentalIndex]: this.selectedMonths[rentalIndex] === month ? null : month
            };
        },
        // ================================================
        // ! 대여 연장
        // ================================================
        async handleExtend(rental) {
            const { value: months } = await this.$swal.fire({
                title: '대여 연장',
                html: `
                    <div style="margin-bottom: 10px;">
                        <strong>${this.isTestUser ? '테스트' : rental.name}</strong>님의 대여를 연장합니다.
                    </div>
                    <div style="margin-bottom: 10px;">
                        현재 종료일: ${this.formatDate(rental.rental_end)}
                    </div>
                `,
                input: 'select',
                inputOptions: {
                    '1': '1개월',
                    '2': '2개월',
                    '3': '3개월',
                    '4': '4개월',
                    '5': '5개월',
                    '6': '6개월'
                },
                inputPlaceholder: '연장할 개월 수를 선택하세요',
                showCancelButton: true,
                confirmButtonText: '연장하기',
                cancelButtonText: '취소',
                inputValidator: (value) => {
                    if (!value) {
                        return '연장할 개월 수를 선택해주세요';
                    }
                }
            });

            if (months) {
                try {
                    const response = await this.$axios.post(`${this.$apiUrls.hanqmedi}/extendRental`, {
                        rental_id: rental.num,
                        extend_months: parseInt(months),
                        companykey: this.$globalState.company_key
                    });

                    if (response.data.success) {
                        await this.$swal.fire({
                            icon: 'success',
                            title: '대여 연장 완료',
                            text: `${months}개월 연장되었습니다.`,
                            confirmButtonText: '확인'
                        });

                        // 데이터 새로고침
                        await this.fetchRentalData();
                    }
                } catch (error) {
                    console.error('대여 연장 중 오류 발생:', error);
                    this.$swal.fire({
                        icon: 'error',
                        title: '대여 연장 실패',
                        text: '대여 연장 처리 중 오류가 발생했습니다.',
                        confirmButtonText: '확인'
                    });
                }
            }
        },
        // ================================================
        // ! 대여 목록 조회
        // ================================================
        async fetchRentalData(tab = this.currentTab) {

            try {
                // 현재 날짜 정보 가져오기
                const now = new Date();
                const currentYear = now.getFullYear();
                const currentMonth = now.getMonth() + 1;

                // 현재 달의 종료일
                const endDate = new Date(currentYear, currentMonth, 0);

                const response = await this.$axios.post(`${this.$apiUrls.hanqmedi}/getRentalList`, {
                    params: {
                        rentalSearch: '',
                        startDate: '',
                        endDate: endDate.toISOString().split('T')[0],
                        companykey: this.$globalState.company_key,
                        page: 1,
                        type: tab,  // 'basic', 'oxygen', 'assistance', 'diabetes'
                        delivery_status: '',
                        payment_type: '',
                        rental_status: '',
                        rental_type: '',
                        contentsSize: 50
                    }
                });

                if (response.data && response.data.rentals) {
                    const rentals = response.data.rentals;
                    const filteredRentals = rentals.filter(rental => {
                        const rentalEndDate = new Date(rental.rental_end);
                        return (
                            rentalEndDate.getFullYear() === currentYear &&
                            rentalEndDate.getMonth() + 1 === currentMonth &&
                            rental.delivery_status !== 4
                        );
                    });

                    // 현재 탭에 따라 데이터 할당
                    switch (tab) {
                        case 'basic':
                            this.basicRentalData = filteredRentals;
                            break;
                        case 'oxygen':
                            this.oxygenRentalData = filteredRentals;
                            break;
                        case 'assistance':
                            this.assistanceRentalData = filteredRentals;
                            break;
                        case 'diabetes':
                            this.diabetesRentalData = filteredRentals;
                            break;
                    }
                } else {
                    throw new Error('데이터 형식이 올바르지 않습니다.');
                }
            } catch (error) {
                console.error('대여 데이터 조회 중 오류 발생:', error);
                this.error = '데이터를 불러오는 중 오류가 발생했습니다.';
            }
        },

        // ================================================
        // ! 대여 목록 조회
        // ================================================
        async fetchAllTabsData() {
            try {
                // 모든 탭의 데이터를 병렬로 불러오기
                await Promise.all([
                    this.fetchRentalData('basic'),
                    this.fetchRentalData('oxygen'),
                    this.fetchRentalData('assistance'),
                    this.fetchRentalData('diabetes')
                ]);
            } catch (error) {
                console.error('전체 탭 데이터 조회 중 오류 발생:', error);
            }
        },

        // ================================================
        // ! 재고 관리 팝업
        // ================================================
        async fetchInventoryData() {
            try {
                const response = await this.$axios.get(`${this.$apiUrls.hanqmedi}/getSafetyStock`, {
                    params: {
                        companykey: this.$globalState.company_key,
                        maxQuantity: 3
                    }
                });

                if (response.data.success) {
                    this.inventoryData = response.data.items.map(item => ({
                        id: item.num,
                        company_name: item.company || '',
                        product_name: item.name3 || item.name,
                        color: item.color || '',
                        size: item.size || '',
                        stock_quantity: item.quantity || 0,
                        safety_stock: 3 // 안전재고 기준
                    }));
                } else {
                    throw new Error('데이터 형식이 올바르지 않습니다.');
                }
            } catch (error) {
                console.error('재고 데이터 조회 중 오류 발생:', error);
                this.error = '데이터를 불러오는 중 오류가 발생했습니다.';
            }
        },

        // ================================================
        // ! 발주 신청 기능
        // ================================================
        handleOrder(item) {
            this.$swal.fire({
                title: '발주 신청',
                html: `
                    <div>거래처 : ${item.company_name}</div>
                    <div>품목 : ${item.product_name}</div>
                    <div>현재 재고 : ${item.stock_quantity}</div>
                `,
                input: 'number',
                inputPlaceholder: '수량을 입력하세요',
                showCancelButton: true,
                confirmButtonText: '발주하기',
                cancelButtonText: '취소',
                inputValidator: (value) => {
                    if (!value || value <= 0) {
                        return '올바른 수량을 입력해주세요';
                    }
                }
            }).then((result) => {
                if (result.isConfirmed) {
                    this.$swal.fire({
                        icon: 'info',
                        title: '개발 진행중',
                        text: '발주 기능은 현재 개발 중입니다.',
                        confirmButtonText: '확인'
                    });
                    // 발주 처리 로직
                    // this.$emit('order', {
                    //     itemId: item.id,
                    //     quantity: result.value,
                    //     companyName: item.company_name,
                    //     productName: item.product_name
                    // });
                }
            });
        },

        // ================================================
        // ! 일괄발주 기능
        // ================================================
        async handleBulkOrder(companyName, items) {
            this.$swal.fire({
                title: '일괄발주 모달을 불러오는 중입니다.',
                allowOutsideClick: false,
                didOpen: () => {
                    this.$swal.showLoading();
                }
            });

            const colorTable = await this.fetchColorTable();

            const { value: formValues } = await this.$swal.fire({
                title: `${companyName} 일괄 발주`,
                html: this.generateBulkOrderForm(items, colorTable),
                width: '900px',
                showCancelButton: true,
                confirmButtonText: '발주하기',
                cancelButtonText: '취소',
                preConfirm: () => {
                    return items.map(item => ({
                        ...item,
                        size: document.getElementById(`size_${item.id}`).value,
                        color: document.getElementById(`color_${item.id}`).value,
                        orderQuantity: parseInt(document.getElementById(`quantity_${item.id}`).value) || 0
                    })).filter(item => item.orderQuantity > 0);
                }
            });

            if (formValues && formValues.length > 0) {
                await this.$swal.fire({
                    icon: 'info',
                    title: '개발 진행중',
                    text: '일괄발주 기능은 현재 개발 중입니다.',
                    confirmButtonText: '확인'
                });

                // try {
                //     this.$swal.fire({
                //         title: '발주 처리중...',
                //         allowOutsideClick: false,
                //         didOpen: () => {
                //             this.$swal.showLoading();
                //         }
                //     });

                //     // API 호출 예시
                //     const response = await this.$axios.post(`${this.$apiUrls.hanqmedi}/bulkOrder`, {
                //         companyName,
                //         orders: formValues,
                //         companykey: this.$globalState.company_key
                //     });

                //     if (response.data.success) {
                //         await this.$swal.fire({
                //             icon: 'success',
                //             title: '발주 완료',
                //             text: '일괄 발주가 성공적으로 처리되었습니다.'
                //         });
                //         await this.fetchInventoryData();
                //     }
                // } catch (error) {
                //     console.error('일괄 발주 처리 중 오류:', error);
                //     this.$swal.fire({
                //         icon: 'error',
                //         title: '발주 실패',
                //         text: '발주 처리 중 오류가 발생했습니다.'
                //     });
                // }
            }
        },

        // ================================================
        // ! 일괄발주 모달
        // ================================================
        generateBulkOrderForm(items, colorTable) {
            const sizeOrder = ['XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL', 'FREE', '소', '중', '대', '특대', '1호', '2호', '3호', '4호', '5호', '1인치', '2인치', '3인치', '4인치', '5인치', '1mm', '2mm', '3mm', '4mm', '5mm'];

            return `
                <div class="bulk-order-form" style="max-height: 580px; overflow-y: auto;">
                    <table style="width: 100%; border-collapse: collapse; margin-top: 10px;">
                        <colgroup>
                            <col style="width: 35%"> <!-- 제품명 -->
                            <col style="width: 10%"> <!-- 사이즈 -->
                            <col style="width: 10%"> <!-- 색상 -->
                            <col style="width: 10%"> <!-- 현재수량 -->
                            <col style="width: 10%"> <!-- 발주수량 -->
                        </colgroup>
                        <thead>
                            <tr>
                                <th style="padding: 5px; border: 1px solid #ddd; position: sticky; top: 0; background: #f8f9fa;">제품명</th>
                                <th style="padding: 5px; border: 1px solid #ddd; position: sticky; top: 0; background: #f8f9fa;">사이즈</th>
                                <th style="padding: 5px; border: 1px solid #ddd; position: sticky; top: 0; background: #f8f9fa;">색상</th>
                                <th style="padding: 5px; border: 1px solid #ddd; position: sticky; top: 0; background: #f8f9fa;">현재수량</th>
                                <th style="padding: 5px; border: 1px solid #ddd; position: sticky; top: 0; background: #f8f9fa;">발주수량</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${items.map(item => `
                                <tr>
                                    <td style="padding: 5px; border: 1px solid #ddd; font-size: 18px;">${item.product_name}</td>
                                    <td style="padding: 5px; border: 1px solid #ddd; font-size: 14px;">
                                        <select id="size_${item.id}" class="swal2-select" style="width: 100%; padding: 4px; border-radius: 5px; border: 1px solid #ddd;">
                                            <option value="${item.size || ''}">${item.size || '선택'}</option>
                                            ${sizeOrder.map(size =>
                size !== item.size ?
                    `<option value="${size}">${size}</option>` :
                    ''
            ).join('')}
                                        </select>
                                    </td>
                                    <td style="padding: 5px; border: 1px solid #ddd; font-size: 14px;">
                                        <select id="color_${item.id}" class="swal2-select" style="width: 100%; padding: 4px; border-radius: 5px; border: 1px solid #ddd;">
                                            <option value="${item.color || ''}">${item.color || '선택'}</option>
                                            ${colorTable.map(color =>
                color.name !== item.color ?
                    `<option value="${color.name}">${color.name}</option>` :
                    ''
            ).join('')}
                                        </select>
                                    </td>
                                    <td style="padding: 5px; border: 1px solid #ddd; text-align: center;">
                                        ${item.stock_quantity}
                                    </td>
                                    <td style="padding: 5px; border: 1px solid #ddd; text-align: center;">
                                        <input type="number" id="quantity_${item.id}" 
                                            min="0" class="swal2-input" 
                                            style="width: 80px; height: 30px; margin: 0; padding: 4px; text-align: center;">
                                    </td>
                                </tr>
                            `).join('')}
                        </tbody>
                    </table>
                </div>
            `;
        },
        // ================================================
        // ! 안전재고 검색
        // ================================================
        async handleSearch() {
            if (!this.searchKeyword.trim()) {
                this.searchCompany = '';
                return;
            }

            // 검색어가 있는 경우
            this.searchCompany = this.searchKeyword;

            // 검색 결과가 없는 경우
            if (Object.keys(this.filteredInventoryData).length === 0) {
                await this.$swal.fire({
                    icon: 'info',
                    title: '검색 결과 없음',
                    text: `'${this.searchKeyword}' 에 대한 검색 결과가 없습니다.`,
                    confirmButtonText: '확인'
                });
                this.searchKeyword = ''; // 검색어 초기화
                this.searchCompany = ''; // 검색 조건 초기화
            }
        },

        // 색상 테이블 조회 메서드
        async fetchColorTable() {
            try {
                const response = await this.$axios.post(`${this.$apiUrls.hanqmedi}/colorTableGet`);

                if (response.data.code === 200) {
                    return response.data.data;
                }
                return [];
            } catch (error) {
                console.error('색상 테이블 조회 중 오류:', error);
                return [];
            }
        },

        closeModal() {
            this.$emit('close')
        },
    },

    async created() {
        // 하루동안 보지 않기 체크
        const userId = this.$globalState?.user_id || 'default';
        const key = `alertModalHide_${this.type}_${userId}`;
        const today = new Date().toISOString().slice(0, 10);
        const hideDate = localStorage.getItem(key);
        if (hideDate === today) {
            // 오늘은 보지 않기 설정됨 → 자동 닫기
            this.$emit('close');
            return;
        }

        if (this.type === 'rental') {
            await this.fetchAllTabsData();
        } else if (this.type === 'inventory') {
            await this.fetchInventoryData();
        }
    },

    watch: {
        show(newVal) {
            if (newVal && this.type === 'rental') {
                this.fetchRentalData();
            } else if (newVal && this.type === 'inventory') {
                this.fetchInventoryData();
            }
        }
    }
}
</script>

<style>
/* 블러 효과를 위한 CSS - 이름과 연락처만 적용 */
.blurred {
    filter: blur(4px) !important;
    pointer-events: auto;
    user-select: auto;
    -webkit-user-select: auto;
    -moz-user-select: auto;
    -ms-user-select: auto;
}
</style>